/* File: src/components/account/AccountPage.js */
import React, { useState, useCallback, useEffect, useRef } from 'react';
// Import Firebase Functions callable utility and the initialized instance
import { functions as functionsInstance } from '../../firebaseConfig'; // IMPORT the instance
import { httpsCallable } from 'firebase/functions';
import { useProfile } from '../../contexts/ProfileContext';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import {
    getAuth,
    signOut,
    EmailAuthProvider,
    reauthenticateWithCredential,
    updateEmail,
    updatePassword,
    sendEmailVerification
} from 'firebase/auth';
// Import constants for Price IDs (used by AccountSubscriptionSection, but good to keep top-level if AccountPage might need them)
// import { PRICE_IDS } from '../../constants/stripeConstants'; // This is now primarily used within AccountSubscriptionSection
// Import FontAwesome for icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEnvelope,
    faLock,
    faCreditCard,
    faSignOutAlt,
    faExclamationTriangle,
    faCheck,
    faExclamationCircle,
    faUser,
    // faSpinner, // Used by LoadingSpinner component directly
    faCheckCircle,
    // faCalendarAlt, // Used by AccountSubscriptionSection directly
    // faCrown, // Used by AccountSubscriptionSection directly
    // faPaperPlane // Used by AccountEmailSection directly
} from '@fortawesome/free-solid-svg-icons';

// --- Child Component Imports ---
import AccountEmailSection from './AccountEmailSection';
import AccountPasswordSection from './AccountPasswordSection';
import AccountSubscriptionSection from './AccountSubscriptionSection';
import AccountLogoutSection from './AccountLogoutSection';

// --- Reusable UI Components (Defined Inline) ---

// Inline spinner for buttons
const LoadingSpinner = ({ whiteText = true }) => (
    <svg className={`animate-spin -ml-1 mr-2 h-4 w-4 ${whiteText ? 'text-white' : 'text-indigo-600'} inline`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);

// Input field component with improved styling
const InputField = ({ id, label, type = 'text', value, onChange, required = false, placeholder = '', disabled = false, error = null }) => (
    <div className="mb-4">
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
            {label}{required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <input
            type={type}
            id={id}
            name={id}
            value={value}
            onChange={onChange}
            required={required}
            placeholder={placeholder}
            disabled={disabled}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors
                ${error ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}
                ${disabled ? 'bg-gray-50 text-gray-500' : 'bg-white'}
            `}
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : undefined}
        />
        {error && (
            <p id={`${id}-error`} className="mt-1 text-xs text-red-600 flex items-center" role="alert">
                <FontAwesomeIcon icon={faExclamationCircle} className="mr-1 h-3 w-3" />
                {error}
            </p>
        )}
    </div>
);

// Section Card component with improved styling
const SectionCard = ({ title, icon, children }) => (
    <div className="bg-white p-4 sm:p-6 border border-gray-200 rounded-lg shadow-sm mb-6 transition-shadow hover:shadow-md">
        <div className="flex items-center mb-4 pb-2 border-b border-gray-200">
            {icon && <FontAwesomeIcon icon={icon} className="mr-3 h-5 w-5 text-indigo-600" />}
            <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        </div>
        {children}
    </div>
);

// Button component for consistent styling
const Button = ({
    onClick,
    disabled = false,
    isLoading = false,
    loadingText = "Processing...",
    text,
    color = "indigo",
    icon = null,
    fullWidth = false,
    type = "button"
}) => {
    const colorVariants = {
        indigo: "bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 text-white",
        blue: "bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 text-white",
        red: "bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white",
        green: "bg-green-600 hover:bg-green-700 focus:ring-green-500 text-white",
        gray: "bg-gray-200 hover:bg-gray-300 focus:ring-gray-400 text-gray-800",
        yellow: "bg-yellow-500 hover:bg-yellow-600 focus:ring-yellow-400 text-white"
    };

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled || isLoading}
            className={`${fullWidth ? 'w-full' : 'w-auto'} px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm
                ${colorVariants[color]}
                disabled:opacity-50 disabled:cursor-not-allowed
                focus:outline-none focus:ring-2 focus:ring-offset-2
                transition-colors duration-150 flex items-center justify-center
                ${isLoading ? 'cursor-wait' : ''}`}
        >
            {isLoading ? (
                <>
                    <LoadingSpinner whiteText={color !== 'gray'} />
                    {loadingText}
                </>
            ) : (
                <>
                    {icon && <FontAwesomeIcon icon={icon} className="mr-2 h-4 w-4" />}
                    {text}
                </>
            )}
        </button>
    );
};

// Status badge component
const StatusBadge = ({ type, text }) => {
    const types = {
        success: "bg-green-100 text-green-800 border border-green-200",
        warning: "bg-yellow-100 text-yellow-800 border border-yellow-200",
        error: "bg-red-100 text-red-800 border border-red-200",
        info: "bg-blue-100 text-blue-800 border border-blue-200",
        neutral: "bg-gray-100 text-gray-800 border border-gray-200"
    };

    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${types[type]}`}>
            {text}
        </span>
    );
};

// Alert component
const Alert = ({ type, message }) => {
    const types = {
        success: {
            bg: "bg-green-50",
            border: "border-green-200",
            text: "text-green-800",
            icon: faCheckCircle
        },
        warning: {
            bg: "bg-yellow-50",
            border: "border-yellow-200",
            text: "text-yellow-800",
            icon: faExclamationTriangle
        },
        error: {
            bg: "bg-red-50",
            border: "border-red-200",
            text: "text-red-800",
            icon: faExclamationCircle
        },
        info: {
            bg: "bg-blue-50",
            border: "border-blue-200",
            text: "text-blue-800",
            icon: faExclamationCircle
        }
    };

    if (!message) return null;
    const style = types[type];
    if (!style) return null; // Handle unknown type

    return (
        <div className={`${style.bg} ${style.text} ${style.border} border rounded-md p-3 mb-4 flex items-start`}>
            <FontAwesomeIcon icon={style.icon} className="mr-2 h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="text-sm">{message}</span>
        </div>
    );
};

// --- Initialize Cloud Function Reference ---
let createCustomerPortalSession;
try {
    if (functionsInstance) {
        createCustomerPortalSession = httpsCallable(functionsInstance, 'createCustomerPortalSession');
        console.log("AccountPage: createCustomerPortalSession function reference obtained.");
    } else {
        console.error("AccountPage: Firebase Functions instance is missing from firebaseConfig.");
    }
} catch (error) {
    console.error("AccountPage: Error getting Firebase Functions reference:", error);
}

const AccountPage = () => {
    const {
        authUser,
        loading: isContextLoading,
        userSubscriptionStatus,
        userSubscriptionEndDate,
        userSubscriptionPriceId
    } = useProfile();
    const navigate = useNavigate();
    const auth = getAuth();

    const [newEmail, setNewEmail] = useState('');
    const [currentPasswordForEmail, setCurrentPasswordForEmail] = useState('');
    const [emailUpdateLoading, setEmailUpdateLoading] = useState(false);
    const [emailUpdateError, setEmailUpdateError] = useState(null);
    const [emailUpdateSuccess, setEmailUpdateSuccess] = useState(false);

    const [currentPasswordForPassword, setCurrentPasswordForPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [passwordUpdateLoading, setPasswordUpdateLoading] = useState(false);
    const [passwordUpdateError, setPasswordUpdateError] = useState(null);
    const [passwordUpdateSuccess, setPasswordUpdateSuccess] = useState(false);

    const [isPortalLoading, setIsPortalLoading] = useState(false);
    const [portalError, setPortalError] = useState(null);
    const componentInstanceId = useRef(`AccountPage-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`).current;

    const [isSendingVerification, setIsSendingVerification] = useState(false);
    const [verificationMessage, setVerificationMessage] = useState(null);
    const [verificationMessageType, setVerificationMessageType] = useState(null);

    useEffect(() => {
        console.log(`[${componentInstanceId} Effect Log] Context Update:`, {
            isContextLoading,
            authUserExists: !!authUser,
            userId: authUser?.uid,
            emailVerified: authUser?.emailVerified,
            userSubscriptionStatus,
            userSubscriptionEndDate: userSubscriptionEndDate?.toISOString(),
            userSubscriptionPriceId
        });
    }, [isContextLoading, authUser, userSubscriptionStatus, userSubscriptionEndDate, userSubscriptionPriceId, componentInstanceId]);

    const reauthenticateUser = useCallback(async (currentPassword) => {
        const user = auth.currentUser;
        if (!user || !user.email) {
            throw new Error("Cannot re-authenticate: User or user email is missing.");
        }
        if (!currentPassword) throw new Error("Current password is required.");

        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        try {
            await reauthenticateWithCredential(user, credential);
            console.log(`[${componentInstanceId}] Re-authentication successful.`);
            return true;
        } catch (error) {
            console.error(`[${componentInstanceId}] Re-authentication failed:`, error);
            if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') throw new Error("Incorrect current password.");
            if (error.code === 'auth/too-many-requests') throw new Error("Too many attempts. Try again later or reset password.");
            throw new Error("Re-authentication failed. Please try again.");
        }
    }, [auth, componentInstanceId]);

    const handleEmailUpdate = useCallback(async (e) => {
        e.preventDefault();
        const trimmedNewEmail = newEmail.trim();
        if (!trimmedNewEmail || !currentPasswordForEmail) { setEmailUpdateError("Please fill in all fields."); return; }
        if (trimmedNewEmail === authUser?.email) { setEmailUpdateError("New email cannot be the same as the current email."); return; }

        setEmailUpdateLoading(true); setEmailUpdateError(null); setEmailUpdateSuccess(false);
        try {
            await reauthenticateUser(currentPasswordForEmail);
            const user = auth.currentUser;
            if (!user) throw new Error("User not found after re-authentication.");
            await updateEmail(user, trimmedNewEmail);

            setEmailUpdateSuccess(true); setNewEmail(''); setCurrentPasswordForEmail('');
            setVerificationMessage(null); setVerificationMessageType(null);
            // alert("Email updated successfully! Please check your new email address for verification."); // Alert moved to child
            setTimeout(() => setEmailUpdateSuccess(false), 3500);
        } catch (error) {
            let message = error.message || "Failed to update email. Please try again.";
            if (error.code === 'auth/email-already-in-use') message = "This email address is already registered to another account.";
            if (error.code === 'auth/invalid-email') message = "The new email address format is invalid.";
            setEmailUpdateError(message);
        } finally {
            setEmailUpdateLoading(false);
        }
    }, [newEmail, currentPasswordForEmail, authUser?.email, reauthenticateUser, componentInstanceId, auth]);

    const handlePasswordUpdate = useCallback(async (e) => {
        e.preventDefault();
        if (!currentPasswordForPassword || !newPassword || !confirmNewPassword) { setPasswordUpdateError("Please fill in all fields."); return; }
        if (newPassword.length < 6) { setPasswordUpdateError("New password must be at least 6 characters long."); return; }
        if (newPassword !== confirmNewPassword) { setPasswordUpdateError("New passwords do not match."); return; }

        setPasswordUpdateLoading(true); setPasswordUpdateError(null); setPasswordUpdateSuccess(false);
        try {
            await reauthenticateUser(currentPasswordForPassword);
            const user = auth.currentUser;
            if (!user) throw new Error("User not found after re-authentication.");
            await updatePassword(user, newPassword);

            setPasswordUpdateSuccess(true); setCurrentPasswordForPassword(''); setNewPassword(''); setConfirmNewPassword('');
            // alert("Password updated successfully!"); // Alert moved to child
            setTimeout(() => setPasswordUpdateSuccess(false), 3500);
        } catch (error) {
            setPasswordUpdateError(error.message || "Failed to update password. Please try again.");
        } finally {
            setPasswordUpdateLoading(false);
        }
    }, [currentPasswordForPassword, newPassword, confirmNewPassword, reauthenticateUser, componentInstanceId, auth]);

    const handleLogout = useCallback(async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error(`[${componentInstanceId}] Logout failed:`, error);
            alert("Logout failed. Please try again.");
        }
    }, [auth, componentInstanceId]);

    const handleManageBilling = useCallback(async () => {
        if (!createCustomerPortalSession) {
            setPortalError("Billing management feature is currently unavailable. Please try again later.");
            return;
        }
        setIsPortalLoading(true); setPortalError(null);
        try {
            const result = await createCustomerPortalSession();
            const portalUrl = result?.data?.url;
            if (portalUrl) {
                window.location.href = portalUrl;
            } else {
                throw new Error("Could not retrieve the billing portal URL.");
            }
        } catch (err) {
            let message = "Failed to open the billing portal. Please try again later.";
            if (err?.code) {
                if (err.code === 'unauthenticated') message = "Authentication error. Please log out and log back in.";
                else if (err.code === 'not-found') message = "Billing information not found for your account.";
                else if (err.code === 'internal') message = err.message || message;
                else message = `Error: ${err.code} - ${err.message}`;
            } else if (err?.message) {
                message = err.message;
            }
            setPortalError(message);
        } finally {
            if (!window.location.href.includes('stripe.com')) {
                setIsPortalLoading(false);
            }
        }
    }, [componentInstanceId, setIsPortalLoading, setPortalError]);

    const handleSendVerificationEmail = useCallback(async () => {
        if (!authUser || !auth.currentUser) {
            setVerificationMessage("Error: Could not find user information. Please refresh.");
            setVerificationMessageType('error');
            return;
        }
        setIsSendingVerification(true); setVerificationMessage(null); setVerificationMessageType(null);
        try {
            await sendEmailVerification(auth.currentUser);
            setVerificationMessage(`Verification email sent to ${authUser.email}. Please check your inbox (and spam folder).`);
            setVerificationMessageType('success');
        } catch (error) {
            let message = "Failed to send verification email. Please try again later.";
            if (error.code === 'auth/too-many-requests') {
                message = "Too many verification emails sent recently. Please wait a while before trying again.";
            }
            setVerificationMessage(message);
            setVerificationMessageType('error');
        } finally {
            setIsSendingVerification(false);
        }
    }, [auth, authUser, componentInstanceId]);

    if (!isContextLoading && !authUser) {
        return <Navigate to="/login" replace />;
    }

    return (
        <div className="p-4 sm:p-6 bg-gray-100 min-h-screen">
            <div className="max-w-2xl mx-auto">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center">
                        <FontAwesomeIcon icon={faUser} className="mr-3 text-indigo-600 h-6 w-6" />
                        Account Management
                    </h1>
                    <button onClick={() => navigate('/dashboard')} className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded px-1" >
                        ← Back to Dashboard
                    </button>
                </div>

                {isContextLoading && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center">
                        <div className="w-12 h-12 mx-auto mb-4 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600">
                            <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <p className="text-gray-600">Loading account details...</p>
                    </div>
                )}

                {!isContextLoading && authUser && (
                    <>
                        <SectionCard title="Manage Email Address" icon={faEnvelope}>
                            <AccountEmailSection
                                authUser={authUser}
                                newEmail={newEmail}
                                setNewEmail={setNewEmail}
                                currentPasswordForEmail={currentPasswordForEmail}
                                setCurrentPasswordForEmail={setCurrentPasswordForEmail}
                                emailUpdateLoading={emailUpdateLoading}
                                emailUpdateError={emailUpdateError}
                                emailUpdateSuccess={emailUpdateSuccess}
                                handleEmailUpdate={handleEmailUpdate}
                                isSendingVerification={isSendingVerification}
                                verificationMessage={verificationMessage}
                                verificationMessageType={verificationMessageType}
                                handleSendVerificationEmail={handleSendVerificationEmail}
                                InputFieldComponent={InputField}
                                ButtonComponent={Button}
                                AlertComponent={Alert}
                                StatusBadgeComponent={StatusBadge}
                            />
                        </SectionCard>

                        <SectionCard title="Change Password" icon={faLock}>
                            <AccountPasswordSection
                                currentPasswordForPassword={currentPasswordForPassword}
                                setCurrentPasswordForPassword={setCurrentPasswordForPassword}
                                newPassword={newPassword}
                                setNewPassword={setNewPassword}
                                confirmNewPassword={confirmNewPassword}
                                setConfirmNewPassword={setConfirmNewPassword}
                                passwordUpdateLoading={passwordUpdateLoading}
                                passwordUpdateError={passwordUpdateError}
                                passwordUpdateSuccess={passwordUpdateSuccess}
                                handlePasswordUpdate={handlePasswordUpdate}
                                InputFieldComponent={InputField}
                                ButtonComponent={Button}
                                AlertComponent={Alert}
                            />
                        </SectionCard>

                        <SectionCard title="Subscription" icon={faCreditCard}>
                            <AccountSubscriptionSection
                                isContextLoading={isContextLoading}
                                userSubscriptionStatus={userSubscriptionStatus}
                                userSubscriptionEndDate={userSubscriptionEndDate}
                                userSubscriptionPriceId={userSubscriptionPriceId}
                                isPortalLoading={isPortalLoading}
                                portalError={portalError}
                                handleManageBilling={handleManageBilling}
                                createCustomerPortalSession={createCustomerPortalSession}
                                ButtonComponent={Button}
                                AlertComponent={Alert}
                                StatusBadgeComponent={StatusBadge}
                                LoadingSpinnerComponent={LoadingSpinner}
                            />
                        </SectionCard>

                        <SectionCard title="Logout" icon={faSignOutAlt}>
                            <AccountLogoutSection
                                handleLogout={handleLogout}
                                ButtonComponent={Button}
                            />
                        </SectionCard>
                    </>
                )}
            </div>
        </div>
    );
};

export default AccountPage;